import {
    Show,
    SimpleShowLayout,
    TextField,
    NumberField,
    Button,
    TopToolbar,
    EditButton,
    DeleteButton,
    useRecordContext,
} from 'react-admin';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Typography, Box, Chip, Alert } from '@mui/material';

const DecodeErrorWarning = () => {
    const record = useRecordContext();
    if (!record?.decodeError) return null;
    return (
        <Alert severity="error" sx={{ mt: 1 }}>
            Arvon purku epäonnistui (msgpack): {record.decodeError}
        </Alert>
    );
};

const StorageTypeChip = () => {
    const record = useRecordContext();
    if (!record?.type) return null;
    const type = record.type as string;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
                Tallennustyyppi:
            </Typography>
            <Chip
                label={
                    type === 'json'
                        ? 'JSON'
                        : type === 'binary'
                          ? 'BINARY'
                          : 'STRING'
                }
                size="small"
                color={
                    type === 'json'
                        ? 'info'
                        : type === 'binary'
                          ? 'secondary'
                          : 'success'
                }
                variant="outlined"
            />
        </Box>
    );
};

const CacheMeta = () => {
    const record = useRecordContext();
    if (!record?.meta) return null;
    const { caller, query, rawQuery, values, cached } = record.meta;
    const queryDisplay =
        typeof query === 'object'
            ? JSON.stringify(query, null, 2)
            : String(query ?? '');
    const rawQueryDisplay = rawQuery
        ? typeof rawQuery === 'object'
            ? JSON.stringify(rawQuery, null, 2)
            : String(rawQuery)
        : null;
    const valuesDisplay = values ? JSON.stringify(values, null, 2) : null;
    const cachedDate = cached
        ? new Date(cached).toLocaleString('fi-FI', {
              dateStyle: 'short',
              timeStyle: 'medium',
          })
        : null;
    return (
        <Box
            sx={{
                mt: 1,
                mb: 2,
                p: 2,
                background: '#e3f2fd',
                borderRadius: '4px',
                border: '1px solid #90caf9',
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Cache metadata
            </Typography>
            {caller && (
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="caption" color="textSecondary">
                        Caller:
                    </Typography>
                    <Chip label={caller} size="small" />
                </Box>
            )}
            {cachedDate && (
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="caption" color="textSecondary">
                        Cached:
                    </Typography>
                    <Typography variant="body2">{cachedDate}</Typography>
                </Box>
            )}
            {query && (
                <Box>
                    <Typography variant="caption" color="textSecondary">
                        Query
                    </Typography>
                    <pre
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            background: '#ffffff',
                            padding: '8px',
                            borderRadius: '4px',
                            maxHeight: '300px',
                            overflow: 'auto',
                            fontSize: '12px',
                            margin: '4px 0 0 0',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        {queryDisplay}
                    </pre>
                </Box>
            )}
            {rawQueryDisplay && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                        Raw Query
                    </Typography>
                    <pre
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            background: '#fff8e1',
                            padding: '8px',
                            borderRadius: '4px',
                            maxHeight: '300px',
                            overflow: 'auto',
                            fontSize: '12px',
                            margin: '4px 0 0 0',
                            border: '1px solid #ffe082',
                        }}
                    >
                        {rawQueryDisplay}
                    </pre>
                </Box>
            )}
            {valuesDisplay && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                        Values
                    </Typography>
                    <pre
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            background: '#ffffff',
                            padding: '8px',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflow: 'auto',
                            fontSize: '12px',
                            margin: '4px 0 0 0',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        {valuesDisplay}
                    </pre>
                </Box>
            )}
        </Box>
    );
};

const ValkeyValue = () => {
    const record = useRecordContext();
    if (!record) return null;
    const display =
        typeof record.value === 'object'
            ? JSON.stringify(record.value, null, 2)
            : String(record.value ?? '');
    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
                Arvo
            </Typography>
            <pre
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    maxHeight: '500px',
                    overflow: 'auto',
                    fontSize: '13px',
                }}
            >
                {display}
            </pre>
        </Box>
    );
};

const formatBytes = (bytes: number): string => {
    if (bytes <= 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
};

const EstimatedSize = () => {
    const record = useRecordContext();
    if (!record) return null;

    const storedSize =
        typeof record.storedSize === 'number' ? record.storedSize : null;
    const jsonSize =
        typeof record.jsonSize === 'number'
            ? record.jsonSize
            : (() => {
                  const serialized =
                      typeof record.value === 'object'
                          ? JSON.stringify(record.value)
                          : String(record.value ?? '');
                  return new TextEncoder().encode(serialized).length;
              })();

    const isBinary = record.type === 'binary';
    const savings =
        isBinary && storedSize && jsonSize && jsonSize > 0
            ? Math.round((1 - storedSize / jsonSize) * 100)
            : null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                flexWrap: 'wrap',
            }}
        >
            <Typography variant="caption" color="textSecondary">
                {isBinary ? 'Tallennettu (binary):' : 'Tallennettu koko:'}
            </Typography>
            <Typography variant="body2">
                {formatBytes(storedSize ?? jsonSize)}
            </Typography>
            {isBinary && (
                <>
                    <Typography variant="caption" color="textSecondary">
                        JSON-koko:
                    </Typography>
                    <Typography variant="body2">
                        {formatBytes(jsonSize)}
                    </Typography>
                    {savings !== null && (
                        <Chip
                            label={`-${savings}%`}
                            size="small"
                            color={savings > 0 ? 'success' : 'default'}
                            variant="outlined"
                        />
                    )}
                </>
            )}
        </Box>
    );
};

const formatMs = (ms: number): string =>
    ms >= 1 ? `${ms.toFixed(2)} ms` : `${(ms * 1000).toFixed(0)} µs`;

const CodecTiming = () => {
    const record = useRecordContext();
    const timing = record?.timing;
    if (!timing) return null;
    const rows: { label: string; pack?: number; parse?: number }[] = [
        { label: 'JSON', pack: timing.jsonPackMs, parse: timing.jsonParseMs },
        {
            label: 'msgpack',
            pack: timing.msgpackPackMs,
            parse: timing.msgpackParseMs,
        },
    ];
    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
                Pack / parse (nopein 3 ajosta)
            </Typography>
            <Box
                component="table"
                sx={{
                    mt: 0.5,
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                    '& td, & th': {
                        border: '1px solid #e0e0e0',
                        padding: '2px 8px',
                        textAlign: 'right',
                    },
                    '& th:first-of-type, & td:first-of-type': {
                        textAlign: 'left',
                    },
                }}
            >
                <thead>
                    <tr>
                        <th></th>
                        <th>Pack</th>
                        <th>Parse</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.label}>
                            <td>{r.label}</td>
                            <td>
                                {typeof r.pack === 'number'
                                    ? formatMs(r.pack)
                                    : '-'}
                            </td>
                            <td>
                                {typeof r.parse === 'number'
                                    ? formatMs(r.parse)
                                    : '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

const ShowActions = () => (
    <TopToolbar>
        <Button label="Hallinta" component={Link} to="/hallinta?tab=1">
            <ArrowBackIcon />
        </Button>
        <EditButton />
        <DeleteButton />
    </TopToolbar>
);

const ValkeyShow = () => (
    <Show
        actions={<ShowActions />}
        queryOptions={{ refetchOnMount: 'always', staleTime: 0 }}
    >
        <SimpleShowLayout>
            <TextField source="key" label="resources.valkey.fields.key" />
            <NumberField source="ttl" label="resources.valkey.fields.ttl" />
            <StorageTypeChip />
            <EstimatedSize />
            <CodecTiming />
            <DecodeErrorWarning />
            <CacheMeta />
            <ValkeyValue />
        </SimpleShowLayout>
    </Show>
);

export default ValkeyShow;
